
import React, { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Container } from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import heroAshram from '@/assets/corporate-wellness/hero-ashram.png';
import outdoorMeditation from '@/assets/corporate-wellness/outdoor-meditation.png';
import masterDevOm from '@/assets/corporate-wellness/master-dev-om.png';
import groupMeditation from '@/assets/corporate-wellness/group-meditation.jpg';

const CorporateWellness: React.FC = () => {
  const enquiryRef = useRef<HTMLDivElement>(null);
  const programsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToEnquiry = () => {
    enquiryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPrograms = () => {
    programsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroAshram} alt="Sacred ashram on the banks of the Ganga in Rishikesh" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <span className="inline-block px-6 py-2 border border-white/40 rounded-full text-sm tracking-widest uppercase mb-8">
            Holistic Corporate Wellness Retreat
          </span>
          <h1 className="text-4xl md:text-6xl font-light mb-2">Find Stillness.</h1>
          <h1 className="text-4xl md:text-6xl font-bold text-ifind-aqua mb-2">Achieve Clarity.</h1>
          <h1 className="text-4xl md:text-6xl font-bold mb-8">Transform Within.</h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Immersive 2 to 7-day retreats at a sacred ashram on the banks of the Ganga in Rishikesh — designed to reset your team from the demands of corporate life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={scrollToEnquiry} className="bg-ifind-aqua hover:bg-ifind-aqua/90 text-white px-8 py-3 text-base">
              Enquire Now
            </Button>
            <Button onClick={scrollToPrograms} variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 text-base">
              Explore Programs
            </Button>
          </div>
        </div>
      </section>

      {/* The Program Section */}
      <section className="py-20 bg-background">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">The Program</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">A Holistic Approach to Corporate Wellness</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-16">
            Rooted in non-ritualistic, modern meditation and mindfulness, our retreat provides practical, life-changing tools for immediate and sustained well-being through intensive internal energy alignment and the balancing of the seven chakras.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: '🧘', title: 'Mindfulness & Mental Resilience', desc: 'Practices to manage high-pressure work culture, reduce burnout, and enhance clarity and focus.' },
              { icon: '⚡', title: 'Chakra Energy Alignment', desc: 'Powerful practices focused on sustainable vitality and the mind-body connection that directly influences core health metrics.' },
              { icon: '🎓', title: 'Expert-Led Training', desc: 'Delivered by highly trained facilitators under the spiritual mentorship of founder Master Dev OM.' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-ifind-offwhite">
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <h3 className="text-xl font-semibold mb-3 text-ifind-charcoal">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Health Markers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'BMI', sub: 'Weight Balance' },
              { label: 'BP', sub: 'Blood Pressure' },
              { label: 'Diabetes', sub: 'Sugar Management' },
              { label: 'Cholesterol', sub: 'Heart Health' },
            ].map((m, i) => (
              <div key={i} className="text-center p-4 rounded-lg border border-ifind-teal/20 bg-white">
                <span className="font-bold text-ifind-teal text-lg">{m.label}</span>
                <p className="text-sm text-muted-foreground mt-1">{m.sub}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Retreat Options */}
      <section ref={programsRef} className="py-20 bg-ifind-offwhite">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">Retreat Options</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Choose Your Transformation</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            Three structured options to meet varying organisational needs. Each program is cumulative, with longer durations offering deeper immersion.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 2-3 Day */}
            <Card className="border-ifind-teal/20 flex flex-col">
              <CardHeader>
                <span className="text-sm text-ifind-teal font-semibold">2-3 Day</span>
                <CardTitle className="text-2xl text-ifind-charcoal">Immersion</CardTitle>
                <p className="text-sm text-muted-foreground">Foundations & Awareness</p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-muted-foreground mb-4">
                  Establishing the basics of modern mindfulness, immediate stress reduction techniques, and an introductory workshop on the Mind-Body Connection & Core Energy Flow.
                </p>
                <p className="text-sm font-semibold mb-2 text-ifind-charcoal">ADDRESSES:</p>
                <p className="text-sm text-muted-foreground mb-4">Immediate Stress Relief, Intro to Mental Wellness & Physical Health Markers</p>
                <p className="text-sm text-muted-foreground mb-6">🧖 One Wellness Spa treatment (3-day option)</p>
                <div className="mt-auto">
                  <Button onClick={scrollToEnquiry} className="w-full bg-ifind-teal hover:bg-ifind-teal/90 text-white">
                    Enquire About This Program
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 5-Day */}
            <Card className="border-ifind-aqua ring-2 ring-ifind-aqua flex flex-col relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ifind-aqua text-white text-xs px-3 py-1 rounded-full font-semibold">
                Most Popular
              </span>
              <CardHeader>
                <span className="text-sm text-ifind-aqua font-semibold">5-Day</span>
                <CardTitle className="text-2xl text-ifind-charcoal">Deep Dive</CardTitle>
                <p className="text-sm text-muted-foreground">Transformation & Integration</p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-muted-foreground mb-4">
                  Includes all 3-Day content, plus advanced meditation techniques, deep-dive workshops on energy management for sustained vitality, weight balance, and Chakra healing, with personalised goal-setting.
                </p>
                <p className="text-sm font-semibold mb-2 text-ifind-charcoal">ADDRESSES:</p>
                <p className="text-sm text-muted-foreground mb-4">Enhanced Mental Wellness, Targeted Physical Health Management (BMI, Diabetes)</p>
                <p className="text-sm text-muted-foreground mb-6">🧖 Two Wellness Spa treatments</p>
                <div className="mt-auto">
                  <Button onClick={scrollToEnquiry} className="w-full bg-ifind-aqua hover:bg-ifind-aqua/90 text-white">
                    Enquire About This Program
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 7-Day */}
            <Card className="border-ifind-teal/20 flex flex-col">
              <CardHeader>
                <span className="text-sm text-ifind-teal font-semibold">7-Day</span>
                <CardTitle className="text-2xl text-ifind-charcoal">Executive Retreat</CardTitle>
                <p className="text-sm text-muted-foreground">Mastery & Sustained Change</p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-muted-foreground mb-4">
                  Includes all 5-Day content, plus one-on-one coaching sessions, deep-dive sessions on chronic stress, hormonal balance, culminating in a comprehensive Wellness Blueprint for long-term corporate integration.
                </p>
                <p className="text-sm font-semibold mb-2 text-ifind-charcoal">ADDRESSES:</p>
                <p className="text-sm text-muted-foreground mb-4">Holistic Transformation, Deep-dive on All Core Physical Markers, Sustainable Change</p>
                <p className="text-sm text-muted-foreground mb-6">🧖 Three Wellness Spa treatments</p>
                <div className="mt-auto">
                  <Button onClick={scrollToEnquiry} className="w-full bg-ifind-teal hover:bg-ifind-teal/90 text-white">
                    Enquire About This Program
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* Curriculum Section */}
      <section className="py-20 bg-background">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">Curriculum</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">What You Will Experience</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-16">
            Five comprehensive modules covering physical, mental, emotional, and spiritual wellness — each building on the last.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              {
                num: '01', title: 'Holistic Wellness',
                items: ['Cleansing of Body & Mind', 'Introduction to Meditation (the correct way of entering and coming out)', 'Understanding the Healthy Plate', 'Gentle movements & yoga', 'Mindfulness Self-Discovery Exercises', 'Guided meditation for inner healing']
              },
              {
                num: '02', title: 'Healing Mind-Body Energy',
                items: ['Yoga and stretching for flexibility and recovery', 'Mindfulness: Emotional regulation and stress management', 'Therapeutic Breathwork', 'Outdoor Active Meditations', 'Chakra Meditations', 'Posture education & exercises for pain management']
              },
              {
                num: '03', title: 'Transformation & Habits',
                items: ['Habit formation and sustainable lifestyle change', 'Bite-Size Exercise daily habit', 'Mindfulness Self-Discovery: Comfort Zone', 'Guided meditation for clarity and peace', 'Dance Fitness', 'Team Activity: Wellness trivia and group activity']
              },
              {
                num: '04', title: 'Emotional Wellness',
                items: ['Emotional intelligence and managing life transitions', 'Stress Management', 'Guided meditations for self-awareness and emotional balance', 'Creative expression building', 'Understanding energy aspects of life phases']
              },
              {
                num: '05', title: 'Long-term Wellness',
                items: ['Long-term wellness daily habits plan', 'Personalised action plan for ongoing wellness', '1:1 follow-up coaching']
              },
            ].map((module) => (
              <div key={module.num} className="p-6 rounded-xl bg-ifind-offwhite border border-ifind-teal/10">
                <span className="text-3xl font-bold text-ifind-aqua/30">{module.num}</span>
                <h3 className="text-lg font-semibold text-ifind-charcoal mt-2 mb-4">{module.title}</h3>
                <ul className="space-y-2">
                  {module.items.map((item, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-ifind-aqua mt-1">✦</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Sample Daily Activities */}
            <div className="p-6 rounded-xl bg-ifind-teal/5 border border-ifind-teal/20">
              <h3 className="text-lg font-semibold text-ifind-charcoal mb-4">Sample Daily Activities</h3>
              <ul className="space-y-2">
                {[
                  'Daily Guided Meditation (OM, Ganga Flow, Kundalini, Vipassana)',
                  'Sufi Heart Healing Breathwork',
                  'Interactive Workshop: Stress & Hormonal Balance',
                  'Chakra Seed/Beej Mantra Meditations',
                  'Movement Meditations',
                  'Sustainable Vitality & Mind-Body Balance Training',
                ].map((item, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-ifind-teal mt-1">●</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* The Setting / Venue */}
      <section className="py-20 bg-ifind-offwhite">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">The Setting</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Sacred Ashram on the Banks of the Ganga</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            Rishikesh — the Yoga Capital of the World. Nestled in a sacred ashram amid towering Himalayas, with lush green grounds, breathtaking sunrises, and serene riverside vibes.
          </p>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img src={outdoorMeditation} alt="Master Dev OM leading outdoor meditation in Rishikesh" className="w-full h-64 md:h-96 object-cover" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🌿', title: 'Healing Air', desc: 'Pure mountain air to rejuvenate the respiratory system and invigorate the body.' },
              { icon: '🌊', title: 'Stress Dissolution', desc: "The Ganga's rhythmic flow helps dissolve accumulated stress and restore inner calm." },
              { icon: '🏔️', title: 'Mental Clarity', desc: 'Himalayan silence aids in mental unwiring and deep focus — a powerful contrast to corporate intensity.' },
              { icon: '🪔', title: 'Cultural Experience', desc: 'Optional evening Ganga Arti gatherings for personal reflection in a neutral, inclusive space.' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-white border border-ifind-teal/10">
                <span className="text-3xl mb-3 block">{item.icon}</span>
                <h3 className="text-lg font-semibold text-ifind-charcoal mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Investment / Pricing */}
      <section className="py-20 bg-background">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">Investment</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">All-Inclusive Pricing</h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            Fixed per-person pricing covering all program modules, accommodation, and meals. Special corporate rates available.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: '2-Day Experience', single: '₹20,000', singleStrike: '₹24,000', double: '₹24,000', desc: 'Foundational modules and immediate stress reduction' },
              { title: '3-Day Immersion', single: '₹30,000', singleStrike: '₹35,000', double: '₹36,000', desc: 'Immersive modules and immediate stress reduction' },
              { title: '5-Day Deep Dive', single: '₹50,000', singleStrike: '₹58,000', double: '₹60,000', desc: 'Advanced health management and energy integration' },
              { title: '7-Day Executive Retreat', single: '₹70,000', singleStrike: '₹78,000', double: '₹84,000', desc: 'One-on-one coaching and comprehensive long-term planning' },
            ].map((plan, i) => (
              <div key={i} className="p-6 rounded-xl border border-ifind-teal/20 bg-white text-center">
                <h3 className="text-lg font-bold text-ifind-charcoal mb-4">{plan.title}</h3>
                <div className="mb-2">
                  <p className="text-sm text-muted-foreground">Ashram Single Room</p>
                  <p className="text-xl font-bold text-ifind-charcoal">{plan.single}<span className="text-sm line-through text-muted-foreground ml-1">{plan.singleStrike}</span></p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">Double AC Room</p>
                  <p className="text-xl font-bold text-ifind-charcoal">{plan.double}</p>
                </div>
                <p className="text-sm text-muted-foreground">{plan.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              <strong>Group Pricing:</strong> The price per person remains consistent regardless of group size. Individual participation is fully supported.
            </p>
            <p className="text-xs text-muted-foreground">
              Note: Government taxes (GST/VAT) are excluded and will be billed separately.
            </p>
          </div>
        </Container>
      </section>

      {/* Master Dev OM Section */}
      <section className="py-20 bg-ifind-offwhite">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-ifind-teal">The Legacy</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Designed by Master Dev OM</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <p className="text-muted-foreground mb-8">
                Master Dev OM is a modern Himalayan yogi and spiritual guide whose meditation methods blend ancient wisdom with contemporary science to create tangible energy shifts, emotional balance, and inner transformation for everyday people.
              </p>
              <ul className="space-y-4">
                {[
                  'Trained directly with Thich Nhat Hanh, the Dalai Lama, and Mother Teresa in mindfulness and spirituality.',
                  'Former IT entrepreneur who left his software export business to dedicate his life to meditation — explaining deep spiritual truths in a clear, modern, and practical way.',
                  'International author of 12 self-help and spiritual books and founder of Soulversity.',
                  'Since 2005, has supported thousands of seekers across 100+ countries to discover their spiritual path.',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-muted-foreground">
                    <span className="text-ifind-aqua mt-1">✦</span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                {[
                  { num: '1,200+', label: 'Teachers Trained' },
                  { num: '100+', label: 'Countries' },
                  { num: '800+', label: 'Coaches Certified' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-bold text-ifind-teal">{stat.num}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <img src={masterDevOm} alt="Master Dev OM" className="rounded-2xl max-h-[500px] object-cover shadow-lg" />
            </div>
          </div>

          <div className="mt-12 p-6 rounded-xl bg-white border border-ifind-teal/10">
            <h3 className="text-lg font-semibold text-ifind-charcoal mb-3">Real Transformation & Life Impact</h3>
            <p className="text-muted-foreground">
              Students report shifts such as emotional healing, stronger boundaries, higher clarity, and a natural movement from anxiety and confusion to grounded confidence and inner celebration. Our programs are delivered by Soulversity Masters, directly trained by Master Dev OM.
            </p>
          </div>
        </Container>
      </section>

      {/* Enquiry Form */}
      <section ref={enquiryRef} className="py-20 bg-ifind-teal">
        <Container>
          <div className="text-center mb-4">
            <span className="text-sm tracking-widest uppercase text-white/70">Get Started</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">Begin Your Team's Transformation</h2>
          <p className="text-white/80 text-center max-w-2xl mx-auto mb-12">
            Fill in the form and our program director will connect with you to discuss the best retreat option for your organisation. We welcome groups of 3 or more, as well as individual participants.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info + Image */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 text-white">
                <span className="text-2xl">📧</span>
                <div>
                  <p className="text-sm text-white/70">Email</p>
                  <a href="mailto:connect@ifindlife.com" className="hover:underline">connect@ifindlife.com</a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white">
                <span className="text-2xl">📱</span>
                <div>
                  <p className="text-sm text-white/70">WhatsApp</p>
                  <a href="https://wa.me/919971608310" target="_blank" rel="noopener noreferrer" className="hover:underline">+91 9971608310</a>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden mt-6 aspect-square">
                <img src={groupMeditation} alt="Group meditation training session at Soulversity" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Form */}
            <EnquiryForm />
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

const EnquiryForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    programInterest: '',
    groupSize: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim()) {
      toast.error('Please fill in your name and email.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('corporate-wellness-enquiry', {
        body: formData,
      });

      if (error) throw error;

      toast.success('Thank you! Our program director will connect with you shortly.');
      setFormData({ fullName: '', email: '', company: '', programInterest: '', groupSize: '', message: '' });
    } catch (err) {
      console.error('Enquiry submission error:', err);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 space-y-4">
      <div className="space-y-2">
        <Label htmlFor="fullName" className="text-ifind-charcoal">Full Name *</Label>
        <Input id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-ifind-charcoal">Email Address *</Label>
        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="company" className="text-ifind-charcoal">Company / Organisation</Label>
        <Input id="company" name="company" value={formData.company} onChange={handleChange} />
      </div>
      <div className="space-y-2">
        <Label className="text-ifind-charcoal">Program of Interest</Label>
        <Select onValueChange={(val) => setFormData(prev => ({ ...prev, programInterest: val }))}>
          <SelectTrigger>
            <SelectValue placeholder="Select a program" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2-day">2-Day Experience</SelectItem>
            <SelectItem value="3-day">3-Day Immersion</SelectItem>
            <SelectItem value="5-day">5-Day Deep Dive</SelectItem>
            <SelectItem value="7-day">7-Day Executive Retreat</SelectItem>
            <SelectItem value="not-sure">Not sure yet</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="groupSize" className="text-ifind-charcoal">Expected Group Size</Label>
        <Input id="groupSize" name="groupSize" value={formData.groupSize} onChange={handleChange} placeholder="e.g. 10-15" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="message" className="text-ifind-charcoal">Message</Label>
        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={3} />
      </div>
      <Button type="submit" disabled={isSubmitting} className="w-full bg-ifind-teal hover:bg-ifind-teal/90 text-white">
        {isSubmitting ? 'Sending...' : 'Send Enquiry'}
      </Button>
    </form>
  );
};

export default CorporateWellness;
